// ── 직원 등록 모달 "새 근무유형 추가" 패널 (2026-08-28 사용자 명세) ──────────────
// 등록 모달 오른쪽 옆에 나란히 뜨는 근무유형 설정 카드. 시간 설정 편집기와 동일한 전체 필드
// (이름·요일별 근무/근무지/출퇴근/휴게/반차기준·1일 소정근로·주간 소정근로+연차 미리보기).
// 공유 Modal 을 쓰지 않는 이유: 공유 Modal 은 중앙 고정만 지원해 "등록 모달 오른쪽" 배치가 불가.
//   배경 오버레이·스크롤 잠금은 이미 열려 있는 등록 모달(공유 Modal)이 담당하므로 이 카드는
//   보조 카드로만 뜬다(자체 잠금 없음). 좁은 화면(<1360px)은 옆 공간이 없어 공유 Modal 로 위에 겹쳐 띄운다.
import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../../../components/Modal';
import { formatTimeTyping } from '../../../utils/timeInput';
import { inputStyle, helperTextStyle } from './styles';
import {
  stdBlackBtn, stdOutlineBtn,
  stdModalHeaderBar, stdModalHeaderIconBox, stdModalHeaderTitle, stdModalCloseBtn,
} from '../adminlayout/designStandard';
import {
  WEEKDAY_ORDER, WEEKDAY_LABELS, createDefaultWeeklyRules, normalizeBreakMinutesInput,
  normalizeDraftWeeklyRules, calcDraftWeeklyScheduledMinutes, isValidHHmm, hhmmToMinutes,
} from '../attendancemgmt/utils';

const SIDE_PANEL_MIN_WIDTH = 1360; // 등록 모달(560) 오른쪽에 패널(≥360)이 들어갈 최소 화면 폭

const dayCardStyle = {
  border: '1px solid var(--border)',
  borderRadius: 10,
  padding: '8px 10px',
  display: 'grid',
  gap: 6,
};
const dayHeadRowStyle = { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' };
const dayTimeRowStyle = { display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' };
const smallInputStyle = { ...inputStyle, width: 64, padding: '6px 8px', fontSize: 13, textAlign: 'center' };
const smallLabelStyle = { fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' };

function defaultRules() {
  // 기존 빠른 추가와 동일한 기본값(월~금 09:00~18:00, 주말 휴무)에서 시작
  return createDefaultWeeklyRules('09:00', '18:00');
}

/** 요일 1개 편집 블록 — 근무 체크 / 근무지 / 출퇴근 / 휴게 / 반차기준 */
function DayRuleEditor({ dayKey, rule, ipAliases, onPatch }) {
  const off = !rule.is_workday;
  return (
    <div style={dayCardStyle}>
      <div style={dayHeadRowStyle}>
        <span style={{ fontWeight: 700, width: 16 }}>{WEEKDAY_LABELS[dayKey]}</span>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={Boolean(rule.is_workday)}
            onChange={(e) => onPatch(dayKey, { is_workday: e.target.checked })}
          />
          근무
        </label>
        <select
          style={{ ...inputStyle, flex: 1, minWidth: 110, padding: '6px 8px', fontSize: 13 }}
          value={rule.work_location || ''}
          disabled={off}
          onChange={(e) => onPatch(dayKey, { work_location: e.target.value || null })}
        >
          <option value="">근무지 미지정</option>
          {ipAliases.map((label) => <option key={label} value={label}>{label}</option>)}
        </select>
      </div>
      <div style={dayTimeRowStyle}>
        <input
          style={smallInputStyle} type="text" inputMode="numeric" maxLength={5}
          placeholder="09:00" value={rule.start_time} disabled={off}
          onChange={(e) => onPatch(dayKey, { start_time: formatTimeTyping(e.target.value) })}
        />
        <span style={{ color: 'var(--text-muted)' }}>~</span>
        <input
          style={smallInputStyle} type="text" inputMode="numeric" maxLength={5}
          placeholder="18:00" value={rule.end_time} disabled={off}
          onChange={(e) => onPatch(dayKey, { end_time: formatTimeTyping(e.target.value) })}
        />
        <span style={smallLabelStyle}>휴게</span>
        <input
          style={{ ...smallInputStyle, width: 48, textAlign: 'right' }} type="text" inputMode="numeric" maxLength={3}
          placeholder="자동" disabled={off}
          value={rule.break_minutes === null || rule.break_minutes === undefined ? '' : rule.break_minutes}
          onChange={(e) => onPatch(dayKey, { break_minutes: e.target.value.replace(/[^0-9]/g, '') })}
        />
        <span style={smallLabelStyle}>분</span>
        <span style={smallLabelStyle}>반차기준</span>
        <input
          style={smallInputStyle} type="text" inputMode="numeric" maxLength={5}
          placeholder="미설정" disabled={off}
          value={rule.half_leave_threshold || ''}
          onChange={(e) => onPatch(dayKey, { half_leave_threshold: formatTimeTyping(e.target.value) })}
        />
      </div>
    </div>
  );
}

export function WorkTypeQuickAddPanel({ open, onClose, onSubmit, existingNames = [], ipAliases = [] }) {
  const [isSide, setIsSide] = useState(window.innerWidth >= SIDE_PANEL_MIN_WIDTH);
  const [name, setName] = useState('');
  const [prescribedHours, setPrescribedHours] = useState('');
  const [rules, setRules] = useState(defaultRules);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onResize = () => setIsSide(window.innerWidth >= SIDE_PANEL_MIN_WIDTH);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // 초기화는 "추가 성공"과 명시적 취소 때만 (2026-08-28 리뷰 반영) — 배경 클릭 등으로 닫혀도
  //   작성 중이던 입력이 남아 있다가 다시 열면 이어서 작성할 수 있다(입력 소실 방지).
  function resetDraft() {
    setName(''); setPrescribedHours(''); setRules(defaultRules()); setError(''); setSaving(false);
  }

  const weeklyMinutes = useMemo(() => calcDraftWeeklyScheduledMinutes(rules), [rules]);
  const hasAnnualLeave = weeklyMinutes >= 15 * 60;

  function patchRule(dayKey, updates) {
    setRules((prev) => ({ ...prev, [dayKey]: { ...prev[dayKey], ...updates } }));
  }

  function validate() {
    const trimmed = name.trim();
    if (!trimmed) return '근무유형 이름을 입력하세요.';
    if (trimmed.length > 20) return '이름은 20자 이하로 해주세요.';
    if (existingNames.includes(trimmed)) return '이미 있는 근무유형입니다.';
    const workdays = WEEKDAY_ORDER.filter((d) => rules[d]?.is_workday);
    if (workdays.length === 0) return '근무 요일을 1개 이상 체크하세요.';
    for (const d of workdays) {
      const r = rules[d];
      if (!isValidHHmm(r.start_time) || !isValidHHmm(r.end_time)) {
        return `${WEEKDAY_LABELS[d]}요일 출퇴근 시간을 HH:MM 으로 입력하세요.`;
      }
      if (hhmmToMinutes(r.end_time) <= hhmmToMinutes(r.start_time)) {
        return `${WEEKDAY_LABELS[d]}요일 퇴근 시간이 출근 시간보다 늦어야 합니다.`;
      }
      if (r.half_leave_threshold && !isValidHHmm(r.half_leave_threshold)) {
        return `${WEEKDAY_LABELS[d]}요일 반차기준을 HH:MM 으로 입력하세요.`;
      }
    }
    return '';
  }

  async function submit() {
    const message = validate();
    if (message) { setError(message); return; }
    setError('');
    setSaving(true);
    try {
      const hours = prescribedHours === '' ? null : Number(prescribedHours);
      const weekly = normalizeDraftWeeklyRules(rules);
      // 서버는 휴게 720분 초과를 400으로 거부 — 기존 시간 설정 편집기와 동일하게 0~720 클램핑
      WEEKDAY_ORDER.forEach((d) => { weekly[d].break_minutes = normalizeBreakMinutesInput(weekly[d].break_minutes); });
      const result = await onSubmit({
        name: name.trim(),
        weekly_rules: weekly,
        prescribed_daily_hours: Number.isFinite(hours) && hours > 0 ? hours : null,
      });
      if (result?.ok) resetDraft();
      else setError(result?.message || '근무유형 추가에 실패했습니다.');
    } catch (e) {
      // 네트워크 단절 등 fetch 예외 — saving 이 true 로 남아 패널이 잠기지 않게 반드시 복구
      setError('저장 요청이 실패했습니다. 네트워크 상태를 확인한 뒤 다시 시도해주세요.');
    } finally {
      setSaving(false);
    }
  }

  const body = (
    <div style={{ display: 'grid', gap: 8 }}>
      <input
        style={inputStyle} placeholder="근무유형 이름*" value={name} autoFocus
        onChange={(e) => setName(e.target.value)}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span style={smallLabelStyle}>1일 소정근로시간</span>
        <input
          style={{ ...smallInputStyle, width: 64, textAlign: 'right' }} type="text" inputMode="decimal"
          placeholder="자동" value={prescribedHours}
          onChange={(e) => setPrescribedHours(e.target.value.replace(/[^0-9.]/g, ''))}
        />
        <span style={smallLabelStyle}>시간 · 비워두면 주 소정근로 ÷ 근무일수 자동 계산</span>
      </div>
      {WEEKDAY_ORDER.map((dayKey) => (
        <DayRuleEditor key={dayKey} dayKey={dayKey} rule={rules[dayKey]} ipAliases={ipAliases} onPatch={patchRule} />
      ))}
      <div style={{ fontSize: 12, fontWeight: 700 }}>
        주간 소정근로: {Math.floor(weeklyMinutes / 60)}시간 {String(weeklyMinutes % 60).padStart(2, '0')}분
        <span style={{ marginLeft: 8, color: hasAnnualLeave ? '#16a34a' : '#dc2626' }}>
          {hasAnnualLeave ? '연차 발생 ✓' : '연차 미발생 ✗ (주 15시간 미만)'}
        </span>
      </div>
      <div style={helperTextStyle}>휴게·반차기준을 비우면 자동 적용됩니다. 저장 후 출퇴근 관리 &gt; 시간 설정에서 언제든 수정할 수 있습니다.</div>
      {error && <div style={{ fontSize: 12, color: '#dc2626', fontWeight: 600 }}>{error}</div>}
    </div>
  );

  const footer = (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
      <button style={stdOutlineBtn} onClick={() => { resetDraft(); onClose(); }} disabled={saving}>취소</button>
      <button style={stdBlackBtn} onClick={submit} disabled={saving}>{saving ? '추가 중...' : '추가'}</button>
    </div>
  );

  if (!open) return null;

  if (!isSide) {
    // 좁은 화면: 등록 모달 위에 공유 Modal 로 겹쳐 띄움 (사용자 확정 2026-08-28)
    return (
      <Modal open={open} title="새 근무유형 추가" icon="⏰" onClose={() => { if (!saving) onClose(); }} footer={footer} mobileSheet>
        {body}
      </Modal>
    );
  }

  // 넓은 화면: 등록 모달(560px 중앙) 오른쪽 옆 고정 카드. z-index 는 공유 Modal 오버레이(1000)보다 위.
  return (
    <div
      style={{
        position: 'fixed',
        left: 'calc(50% + 296px)',
        top: '50%',
        transform: 'translateY(-50%)',
        // min(calc()) 대신 width+maxWidth — 구형 사파리에서 min() 안 계산 시 카드가 콘텐츠 폭으로 커지는 것 방지
        width: 460,
        maxWidth: 'calc(50vw - 312px)',
        maxHeight: '86vh',
        zIndex: 1001,
        display: 'flex',
        flexDirection: 'column',
      }}
      className="bg-white dark:bg-[var(--bg-card)] rounded-2xl border border-[var(--border)] shadow-[0_24px_64px_rgba(0,0,0,0.16),0_4px_16px_rgba(0,0,0,0.08)]"
      onClick={(e) => e.stopPropagation()}
    >
      <div style={stdModalHeaderBar(16)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <div style={stdModalHeaderIconBox} aria-hidden="true">⏰</div>
          <span style={stdModalHeaderTitle}>새 근무유형 추가</span>
        </div>
        <button
          type="button"
          style={stdModalCloseBtn}
          onClick={() => { if (!saving) onClose(); }}
          aria-label="닫기"
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M2 2l12 12M14 2L2 14" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
          </svg>
        </button>
      </div>
      <div style={{ padding: '14px 18px', overflowY: 'auto', flex: 1, minWidth: 0 }}>{body}</div>
      <div style={{ padding: '10px 18px', borderTop: '1px solid var(--border)', flexShrink: 0 }}>{footer}</div>
    </div>
  );
}

export default WorkTypeQuickAddPanel;
